<template>
  <div class="banner-wrapper">
    <!-- 首页轮播banner(可包含视频以及图片) -->
    <div class="swiper-container" id="mySwiper">
      <div class="swiper-wrapper">
        <div class="swiper-slide" v-for="(item, index) in bannerList" :key="index">
          <!-- 视频轮播项 -->
          <div v-if="item.type === 'video'" class="slide-content video-slide">
            <video class="banner-video" :src="item.url" autoplay muted playsinline
                   @loadedmetadata="handleVideoLoaded($event, index)"
                   @ended="handleVideoEnded(index)"></video>
            <div class="banner-overlay"></div>
            <div class="banner-content">
              <h2 class="banner-title">{{item.title}}</h2>
              <p class="banner-subtitle">{{item.subTitle}}</p>
            </div>
          </div>
          <!-- 图片轮播项 -->
          <div v-else class="slide-content image-slide">
            <img class="banner-image" :src="item.url" alt="banner">
            <div class="banner-overlay"></div>
            <div class="banner-content">
              <h2 class="banner-title">{{item.title}}</h2>
              <p class="banner-subtitle">{{item.subTitle}}</p>
            </div>
          </div>
        </div>
      </div>
      <!-- 水平分页器 -->
      <div class="swiper-tool">
        <span :class="[currentIndex===idx?'swiper-pagination-bullet-active' : '']" v-for="(i,idx) in bannerList"></span>
      </div>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper';
import {getBanner} from '@/api';
import {mapState} from "vuex";

export default {
  name: 'HomeBanner',
  props: {
    bannerData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      bannerList: [],
      swiper: null,
      currentIndex: 0,
      autoplayDuration: 5000, // 默认轮播时间（毫秒）
      currentVideoIndex: null,
      timer: null // 新增
    }
  },
  watch: {
    bannerData: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.bannerList = newVal;
          this.$nextTick(() => {
            this.initSwiper();
          });
        }
      },
      immediate: false
    }
  },
  created() {
  },
  mounted() {
    this.$nextTick(() => {
      this.initSwiper();
    });
  },
  beforeDestroy() {
    // 销毁swiper实例
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  },
  methods: {
    // 初始化swiper
    initSwiper() {
      if (this.bannerList.length === 0) return;

      if (this.swiper) {
        this.swiper.destroy(true, true);
      }

      this.swiper = new Swiper('#mySwiper', {
        direction: 'horizontal',
        loop: false,
        autoplay: true,
        on: {
          slideChange: () => {
            this.handleSlideChange();
          }
        }
      });
    },

    // 处理视频加载完成事件
    handleVideoLoaded(event, index) {
      const video = event.target;
      // 如果是视频，则设置轮播时间为视频时长
      if (video && video.duration) {
        if (this.swiper && this.swiper.realIndex === index) {
          this.currentVideoIndex = index;
          // 暂停自动轮播，等待视频播放完成
          this.swiper.autoplay.stop();
          // 视频播放
          video.play();
        }
      }
    },

    // 处理视频播放结束事件
    handleVideoEnded(index) {
      if (this.swiper && this.currentVideoIndex === index) {
        // 视频播放完成后，切换到下一张
        this.swiper.slideNext();
        // 重新启用自动轮播
        this.swiper.autoplay.start();
        this.currentVideoIndex = null;
      }
    },

    // 处理轮播切换事件
    handleSlideChange() {
      if (!this.swiper) return;

      // 切换 slide 时，清除上一个定时器
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }

      const realIndex = this.swiper.realIndex;
      const currentSlide = this.bannerList[realIndex];
      this.currentIndex = realIndex
      if (currentSlide && currentSlide.type === 'video') {
        const videoElement = this.swiper.slides[this.swiper.activeIndex].querySelector('video');
        if (videoElement) {
          this.currentVideoIndex = realIndex;
          videoElement.currentTime = 0;
          // 捕获 play() 的异常
          videoElement.play().then(() => {
            // 正常播放
            videoElement.onended = () => {
              this.swiper.slideNext();
            };
          }).catch((e) => {
            // 播放失败（如页面在后台），直接切到下一张
            console.warn('视频播放被中断:', e);
            this.swiper.slideNext();
          });
        }
      } else {
        // 图片 slide，定时切换
        this.timer = setTimeout(() => {
          this.swiper.slideNext();
        }, this.autoplayDuration);
        this.currentVideoIndex = null;
      }
    },

    goToSlide(index) {
      if (this.swiper) {
        if (this.swiper.params.loop) {
          this.swiper.slideToLoop(index);
        } else {
          this.swiper.slideTo(index);
        }
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '../assets/styles/variables.less';
@import '../assets/styles/mixins.less';

.banner-wrapper {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;

  .swiper-container {
    width: 100%;
    height: 100%;
  }

  .swiper-slide {
    width: 100%;
    height: 100%;

    .slide-content {
      position: relative;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }

    .banner-video,
    .banner-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .banner-overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.4);
      z-index: 2;
    }

    .banner-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 3;
      color: #fff;
      text-align: center;
      width: 80%;

      .banner-title {
        font-size: 24px;
        font-weight: bold;
        margin-bottom: 10px;
        line-height: 1.2;
      }

      .banner-subtitle {
        font-size: 12px;
        font-weight: 300;
        line-height: 1.4;
      }
    }
  }

  .swiper-tool {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    bottom: 20px;
    position: absolute;
    z-index: 10;
    gap: 10px;
      span {
        display: inline-block !important;
        width: 8px;
        height: 8px;
        background: rgba(255,255,255,0.5);
        border-radius: 50%;
        border: none;
      }
    .swiper-pagination-bullet-active {
      width: 8px !important;
      height: 8px !important;
      background: transparent !important;
      border: 2px solid #fff !important;
      opacity: 1 !important;
    }
  }
}
</style>